import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { blogService, settingsService } from '@/lib/firebase';

const BlogAdmin = ({ isOpen, onClose, onRefresh }) => {
  const [password, setPassword] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [activeTab, setActiveTab] = useState('create');
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [siteSettings, setSiteSettings] = useState({ bookingUrl: '' });
  
  const today = new Date().toISOString().split('T')[0];
  
  const [formData, setFormData] = useState({
    headline: '',
    article: '',
    ctaText: '',
    ctaLink: '#',
    scheduledDate: today
  });

  const handleUnlock = (e) => {
    e.preventDefault();
    if (password === 'elite27') {
      setIsUnlocked(true);
      fetchPosts();
      fetchSettings();
    } else {
      alert('Incorrect password');
      setPassword('');
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await blogService.getPosts(true);
      setPosts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    const settings = await settingsService.getSettings();
    setSiteSettings(settings);
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await settingsService.updateSettings(siteSettings);
      alert('Settings updated successfully');
      onRefresh();
    } catch (err) {
      alert('Error updating settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await blogService.updatePost(editingId, formData);
      } else {
        await blogService.addPost(formData);
      }
      resetForm();
      setActiveTab('manage');
      fetchPosts();
      onRefresh();
    } catch (err) {
      alert('Error saving post');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ headline: '', article: '', ctaText: '', ctaLink: '#', scheduledDate: today });
    setEditingId(null);
  };

  const handleEdit = (post) => {
    setFormData({
      headline: post.headline,
      article: post.article,
      ctaText: post.ctaText,
      ctaLink: post.ctaLink,
      scheduledDate: post.scheduledDate || today
    });
    setEditingId(post.id);
    setActiveTab('create');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    setLoading(true);
    try {
      await blogService.deletePost(id);
      fetchPosts();
      onRefresh();
    } catch (err) {
      alert('Error deleting post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-navy/95 backdrop-blur-md">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-navy-lighter border border-white/10 rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
          >
            <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
              <h2 className="text-2xl font-anton uppercase tracking-wide text-white">
                {!isUnlocked ? 'Admin Access' : 'Control Center'}
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
              </button>
            </div>

            {isUnlocked && (
              <div className="flex border-b border-white/5 bg-navy/50 overflow-x-auto">
                <button 
                  onClick={() => { setActiveTab('create'); resetForm(); }}
                  className={`flex-1 min-w-[120px] py-4 text-xs font-bold uppercase tracking-widest transition-colors ${activeTab === 'create' ? 'text-gold border-b-2 border-gold bg-white/5' : 'text-gray-500 hover:text-gray-300'}`}
                >
                  {editingId ? 'Edit Post' : 'Create Post'}
                </button>
                <button 
                  onClick={() => { setActiveTab('manage'); fetchPosts(); }}
                  className={`flex-1 min-w-[120px] py-4 text-xs font-bold uppercase tracking-widest transition-colors ${activeTab === 'manage' ? 'text-gold border-b-2 border-gold bg-white/5' : 'text-gray-500 hover:text-gray-300'}`}
                >
                  Manage Posts
                </button>
                <button 
                  onClick={() => { setActiveTab('settings'); fetchSettings(); }}
                  className={`flex-1 min-w-[120px] py-4 text-xs font-bold uppercase tracking-widest transition-colors ${activeTab === 'settings' ? 'text-gold border-b-2 border-gold bg-white/5' : 'text-gray-500 hover:text-gray-300'}`}
                >
                  General Settings
                </button>
              </div>
            )}

            <div className="p-8 overflow-y-auto">
              {!isUnlocked ? (
                <form onSubmit={handleUnlock} className="space-y-6 max-w-md mx-auto py-12">
                  <div className="text-center mb-8">
                    <SafeIcon icon={FiIcons.FiLock} className="text-5xl text-gold mx-auto mb-4" />
                    <p className="text-gray-400 font-medium">Enter credentials to manage content.</p>
                  </div>
                  <input 
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-navy border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold"
                    placeholder="Password"
                    autoFocus
                  />
                  <button className="w-full py-4 bg-gold text-navy font-bold uppercase tracking-widest rounded-lg hover:bg-gold-hover transition-all">
                    Unlock Admin
                  </button>
                </form>
              ) : activeTab === 'create' ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Headline</label>
                      <input 
                        required
                        value={formData.headline}
                        onChange={(e) => setFormData({...formData, headline: e.target.value})}
                        className="w-full bg-navy border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold"
                        placeholder="Article Title..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Publish Date (1am CST)</label>
                      <input 
                        required
                        type="date"
                        value={formData.scheduledDate}
                        onChange={(e) => setFormData({...formData, scheduledDate: e.target.value})}
                        className="w-full bg-navy border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Article Content</label>
                    <textarea 
                      required
                      rows={8}
                      value={formData.article}
                      onChange={(e) => setFormData({...formData, article: e.target.value})}
                      className="w-full bg-navy border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold resize-none"
                      placeholder="Paste your article content here..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">CTA Button Text</label>
                      <input 
                        required
                        value={formData.ctaText}
                        onChange={(e) => setFormData({...formData, ctaText: e.target.value})}
                        className="w-full bg-navy border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold"
                        placeholder="e.g. Book a Demo"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">CTA Link (Leave '#' for global booking)</label>
                      <input 
                        value={formData.ctaLink}
                        onChange={(e) => setFormData({...formData, ctaLink: e.target.value})}
                        className="w-full bg-navy border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold"
                        placeholder="e.g. #booking"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-white/5">
                    {editingId && (
                      <button 
                        type="button"
                        onClick={() => { setEditingId(null); setActiveTab('manage'); }}
                        className="flex-1 py-4 bg-white/5 text-white font-bold uppercase tracking-widest rounded-lg hover:bg-white/10 transition-all"
                      >
                        Cancel
                      </button>
                    )}
                    <button 
                      disabled={loading}
                      className="flex-1 py-4 bg-gold text-navy font-bold uppercase tracking-widest rounded-lg hover:bg-gold-hover disabled:opacity-50 transition-all shadow-[0_10px_20px_rgba(245,166,35,0.2)]"
                    >
                      {loading ? 'Processing...' : editingId ? 'Update Article' : 'Publish Article'}
                    </button>
                  </div>
                </form>
              ) : activeTab === 'settings' ? (
                <form onSubmit={handleUpdateSettings} className="space-y-8 py-4">
                  <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                    <h3 className="text-white font-anton uppercase tracking-wider mb-6 flex items-center gap-2">
                       <SafeIcon icon={FiIcons.FiSettings} className="text-gold" />
                       Global Redirection
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Global Booking URL</label>
                        <input 
                          required
                          value={siteSettings.bookingUrl}
                          onChange={(e) => setSiteSettings({...siteSettings, bookingUrl: e.target.value})}
                          className="w-full bg-navy border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold"
                          placeholder="e.g. https://calendly.com/your-team"
                        />
                        <p className="mt-2 text-[10px] text-gray-500 uppercase tracking-widest font-bold">
                          All "Book Demo" buttons will redirect to this link automatically.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button 
                    disabled={loading}
                    className="w-full py-4 bg-gold text-navy font-bold uppercase tracking-widest rounded-lg hover:bg-gold-hover disabled:opacity-50 transition-all"
                  >
                    {loading ? 'Saving...' : 'Save Global Settings'}
                  </button>
                </form>
              ) : (
                <div className="space-y-4">
                  {loading ? (
                    <p className="text-center text-gray-500 py-12">Loading posts...</p>
                  ) : posts.length === 0 ? (
                    <div className="text-center py-12">
                      <SafeIcon icon={FiIcons.FiInbox} className="text-4xl text-gray-700 mx-auto mb-4" />
                      <p className="text-gray-500">No posts found in your database.</p>
                    </div>
                  ) : (
                    posts.map(post => (
                      <div key={post.id} className="bg-navy border border-white/5 rounded-xl p-5 flex justify-between items-center group hover:border-gold/30 transition-all">
                        <div className="pr-4">
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="text-white font-bold line-clamp-1">{post.headline}</h4>
                            {new Date().toISOString() >= post.publishDate ? (
                              <span className="text-[10px] bg-green-500/10 text-green-400 px-2 py-0.5 rounded-full uppercase font-bold tracking-tighter border border-green-500/20">Published</span>
                            ) : (
                              <span className="text-[10px] bg-gold/10 text-gold px-2 py-0.5 rounded-full uppercase font-bold tracking-tighter border border-gold/20">Scheduled</span>
                            )}
                          </div>
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">
                            Date: {post.displayDate}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handleEdit(post)} className="p-2.5 bg-white/5 text-blue-400 rounded-lg hover:bg-white/10 transition-colors" title="Edit">
                            <SafeIcon icon={FiIcons.FiEdit2} />
                          </button>
                          <button onClick={() => handleDelete(post.id)} className="p-2.5 bg-white/5 text-red-400 rounded-lg hover:bg-white/10 transition-colors" title="Delete">
                            <SafeIcon icon={FiIcons.FiTrash2} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BlogAdmin;